import type { Directive } from 'vue'

/**
 * IME 安全的 Enter（用 `v-enter="fn"` 取代 `@keyup.enter="fn"`）。
 *
 * 中文輸入法選字時按 Enter 只是「確認候選詞」：那一下的 keydown 是 `key='Process'`／
 * `keyCode 229`／`isComposing=true`，而 keyup 已經在 compositionend **之後**（`isComposing`
 * 又變回 false）——所以 `@keyup.enter` 分不出「選字」和「送出」，客戶名稱打到一半就被送出去
 * （2026-09-02 主人回報）。改聽 keydown 並排除組字中的事件，跨 Chrome／Firefox／Safari 都對。
 *
 * 用法：`v-enter="createCustomer"`（函式）或 `v-enter="() => saveLabel(h)"`（帶參數）。
 * 會 `preventDefault()`——在 `<form>` 內不會再觸發原生 submit（避免與 `@submit` 重複送兩次）。
 */
type Handler = () => void
const fns = new WeakMap<HTMLElement, Handler>()
const listeners = new WeakMap<HTMLElement, (e: KeyboardEvent) => void>()

export const vEnter: Directive<HTMLElement, Handler> = {
  mounted(el, binding) {
    fns.set(el, binding.value)
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing || e.keyCode === 229) return
      e.preventDefault()
      fns.get(el)?.()
    }
    listeners.set(el, onKeydown)
    el.addEventListener('keydown', onKeydown)
  },
  updated(el, binding) {
    // 綁定值是 closure（`() => saveLabel(h)`）時每次 render 都是新函式，要跟著換。
    fns.set(el, binding.value)
  },
  unmounted(el) {
    const l = listeners.get(el)
    if (l) el.removeEventListener('keydown', l)
    listeners.delete(el)
    fns.delete(el)
  },
}
